import { inject, Injectable, signal, TemplateRef } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  createUrlTreeFromSnapshot,
  NavigationCancel,
  NavigationError,
  Router,
  RoutesRecognized,
  UrlTree
} from '@angular/router';
import { filter } from 'rxjs';
import { MoUtils } from '@mogenius/client-sdk/utils';

export interface HeaderSlotContent {
  template: TemplateRef<unknown>;
  maxWidth?: string;
}

@Injectable({
  providedIn: 'root'
})
export class PageHeaderService {
  private readonly _router = inject(Router);

  private readonly _contentLeft = signal<HeaderSlotContent | null>(null);
  private readonly _contentCenter = signal<HeaderSlotContent | null>(null);
  private readonly _contentRight = signal<HeaderSlotContent | null>(null);
  // Per-route header visibility and back target, set via data.meta.hideHeader / data.meta.backLink
  // (plus data.meta.backQueryParams when the target needs them). hideHeader may also be a function of the
  // route snapshot, for a route whose query switches between a list and an object view (Storage).
  private readonly _hideHeader = signal<boolean>(false);
  private readonly _backLink = signal<UrlTree | null>(null);

  public readonly contentLeft = this._contentLeft.asReadonly();
  public readonly contentCenter = this._contentCenter.asReadonly();
  public readonly contentRight = this._contentRight.asReadonly();
  public readonly hideHeader = this._hideHeader.asReadonly();
  public readonly backLink = this._backLink.asReadonly();

  constructor() {
    // Resolve on RoutesRecognized — fires before the new view is activated/painted.
    // This service is eagerly created at app start (see app.config), so it also
    // catches the initial navigation; the header therefore never paints a stale
    // visibility or back target on reload or in-app navigation. A guard or error can still
    // abort that navigation; then the values go back to the route that stays active.
    this._applyRouteMeta(this._router.routerState.snapshot.root);
    this._router.events
      .pipe(
        filter(
          (event) =>
            event instanceof RoutesRecognized || event instanceof NavigationCancel || event instanceof NavigationError
        )
      )
      .subscribe((event) =>
        this._applyRouteMeta(
          event instanceof RoutesRecognized ? event.state.root : this._router.routerState.snapshot.root
        )
      );
  }

  private _applyRouteMeta(root: ActivatedRouteSnapshot): void {
    let route: ActivatedRouteSnapshot | null = root;
    let hide = false;
    let backLink: UrlTree | null = null;
    while (route) {
      if (route.outlet === 'primary') {
        const meta = route.data?.['meta'];
        const hideHeader = meta?.['hideHeader'];
        if (typeof hideHeader === 'function') {
          hide = hideHeader(route);
        } else if (hideHeader !== undefined) {
          hide = MoUtils.parseBoolean(hideHeader, false);
        }
        // Commands resolve against the route that declares them: ['..'] leads to its parent.
        if (meta?.['backLink']) {
          backLink = createUrlTreeFromSnapshot(route, meta['backLink'], meta['backQueryParams'] ?? null);
        }
      }
      route = route.firstChild;
    }
    this._hideHeader.set(hide);
    this._backLink.set(backLink);
  }

  public setContentLeft(template: TemplateRef<unknown>, maxWidth?: string): void {
    this._contentLeft.set({ template, maxWidth });
  }

  public setContentCenter(template: TemplateRef<unknown>, maxWidth?: string): void {
    this._contentCenter.set({ template, maxWidth });
  }

  public setContentRight(template: TemplateRef<unknown>, maxWidth?: string): void {
    this._contentRight.set({ template, maxWidth });
  }

  public clearContentLeft(template: TemplateRef<unknown>): void {
    if (this._contentLeft()?.template === template) {
      this._contentLeft.set(null);
    }
  }

  public clearContentCenter(template: TemplateRef<unknown>): void {
    if (this._contentCenter()?.template === template) {
      this._contentCenter.set(null);
    }
  }

  public clearContentRight(template: TemplateRef<unknown>): void {
    if (this._contentRight()?.template === template) {
      this._contentRight.set(null);
    }
  }
}
